import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateRange } from 'react-date-range';
import { addDays, eachDayOfInterval, format, differenceInCalendarDays } from 'date-fns';
import { de } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  Users,
  Bath,
  Calendar,
  BedDouble,
  Trees,
  Waves,
  Sofa,
  MapPin,
  Plane,
  Train,
  ChevronLeft,
  ChevronRight,
  Lock,
  LockOpen,
  LogOut,
  X,
  Trash2,
  Wallet,
  PenLine,
  Plus,
} from 'lucide-react';

const ADMIN_SESSION_KEY = 'mascapon_admin_session_v1';

function loadAdminSession() {
  try {
    const raw = sessionStorage.getItem(ADMIN_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.unlocked && typeof parsed.password === 'string') {
      return { password: parsed.password };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function saveAdminSession(password) {
  sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify({ unlocked: true, password }));
}

function clearAdminSession() {
  sessionStorage.removeItem(ADMIN_SESSION_KEY);
}

function formatEur(value) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return null;
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value));
}

function legacyWeeklyToTotal(booking) {
  const start = new Date(booking.startDate);
  const end = new Date(booking.endDate);
  const nights = Math.max(1, differenceInCalendarDays(end, start) + 1);
  const weeks = nights / 7;
  const weekly = booking.weeklyRent != null ? Number(booking.weeklyRent) : NaN;
  if (!Number.isFinite(weekly) || weekly <= 0) return '';
  return String(Math.round(weekly * weeks * 100) / 100);
}

function getBookingFinancials(booking) {
  const start = new Date(booking.startDate);
  const end = new Date(booking.endDate);
  const nights = Math.max(1, differenceInCalendarDays(end, start) + 1);
  const weeks = nights / 7;
  const paid = booking.amountPaid != null ? Number(booking.amountPaid) : 0;

  const totalRent = booking.totalRent != null ? Number(booking.totalRent) : NaN;
  if (Number.isFinite(totalRent) && totalRent > 0) {
    const totalAgreed = Math.round(totalRent * 100) / 100;
    const remaining = Math.max(0, Math.round((totalAgreed - paid) * 100) / 100);
    return { nights, weeks, paid, totalAgreed, remaining, hasFinance: true };
  }

  const weekly = booking.weeklyRent != null ? Number(booking.weeklyRent) : NaN;
  const hasLegacyWeekly = Number.isFinite(weekly) && weekly > 0;
  const totalAgreed = hasLegacyWeekly ? Math.round(weekly * weeks * 100) / 100 : null;
  const remaining =
    totalAgreed != null ? Math.max(0, Math.round((totalAgreed - paid) * 100) / 100) : null;
  return { nights, weeks, paid, totalAgreed, remaining, hasFinance: !!hasLegacyWeekly };
}

function BookingHoverCard({ calendarHover }) {
  const b = calendarHover.booking;
  const fin = getBookingFinancials(b);
  const left = Math.min(
    calendarHover.x + 14,
    typeof window !== 'undefined' ? window.innerWidth - 280 : 0
  );
  return (
    <div
      className="fixed z-[70] pointer-events-none max-w-xs rounded-xl border border-provence-stone/10 bg-provence-stone text-white shadow-2xl px-4 py-3 text-left text-xs leading-relaxed"
      style={{ left, top: calendarHover.y + 14 }}
    >
      <p className="font-serif text-sm font-semibold text-white mb-1">{b.label || 'Belegt'}</p>
      <p className="text-white/75 mb-2">
        {format(new Date(b.startDate), 'dd.MM.yyyy', { locale: de })} –{' '}
        {format(new Date(b.endDate), 'dd.MM.yyyy', { locale: de })}
      </p>
      <div className="border-t border-white/15 pt-2 space-y-1.5">
        <p className="text-[11px] uppercase tracking-wide text-white/50">Finanzen</p>
        {fin.hasFinance ? (
          <>
            <div className="flex justify-between gap-4">
              <span className="text-white/70">Gesamt</span>
              <span>{formatEur(fin.totalAgreed)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-white/70">Angezahlt</span>
              <span>{formatEur(fin.paid)}</span>
            </div>
            <div className="flex justify-between gap-4 font-medium text-provence-lavender/90">
              <span>Offen</span>
              <span>{formatEur(fin.remaining)}</span>
            </div>
          </>
        ) : (
          <p className="text-white/60">Keine Finanzdaten hinterlegt.</p>
        )}
      </div>
    </div>
  );
}

const PropertyDetail = () => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  // Buchungen vom Backend
  const [bookings, setBookings] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState(new Set());

  // Admin-Modal
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLabel, setAdminLabel] = useState('');
  const [adminTotalRent, setAdminTotalRent] = useState('');
  const [adminAmountPaid, setAdminAmountPaid] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);
  const [calendarHover, setCalendarHover] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [editingBookingId, setEditingBookingId] = useState(null);
  const [dateRange, setDateRange] = useState([
    { startDate: new Date(), endDate: addDays(new Date(), 7), key: 'selection' }
  ]);

  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const weekDays = ['M', 'D', 'M', 'D', 'F', 'S', 'S'];

  const getDaysInMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();
  // Returns 0 for Monday, 1 for Tuesday, ..., 6 for Sunday (ISO week)
  const getStartDayOfMonth = (year, monthIndex) => {
    const jsDay = new Date(year, monthIndex, 1).getDay(); // 0=Sun,1=Mon,...,6=Sat
    return (jsDay + 6) % 7; // shift so 0=Mon
  };

  // Buchungen laden
  const fetchBookings = async () => {
    try {
      const res = await fetch('/api/bookings');
      const data = await res.json();
      setBookings(data);

      const dates = new Set();
      data.forEach((booking) => {
        const start = new Date(booking.startDate);
        const end = new Date(booking.endDate);
        eachDayOfInterval({ start, end }).forEach((d) => {
          dates.add(format(d, 'yyyy-MM-dd'));
        });
      });
      setUnavailableDates(dates);
    } catch (e) {
      console.error('Fehler beim Laden der Buchungen', e);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    const session = loadAdminSession();
    if (session) {
      setAdminUnlocked(true);
      setAdminPassword(session.password);
    }
  }, []);

  const dateToBooking = useMemo(() => {
    const map = new Map();
    bookings.forEach((booking) => {
      const start = new Date(booking.startDate);
      const end = new Date(booking.endDate);
      eachDayOfInterval({ start, end }).forEach((d) => {
        map.set(format(d, 'yyyy-MM-dd'), booking);
      });
    });
    return map;
  }, [bookings]);

  const previewFinancials = useMemo(() => {
    const start = dateRange[0]?.startDate;
    const end = dateRange[0]?.endDate;
    if (!start || !end) return null;
    const nights = Math.max(1, differenceInCalendarDays(end, start) + 1);
    const raw = String(adminTotalRent).replace(',', '.').trim();
    const total = raw === '' ? NaN : Number(raw);
    if (!Number.isFinite(total) || total <= 0) {
      return { nights, totalAgreed: null, paid: null, remaining: null };
    }
    const totalAgreed = Math.round(total * 100) / 100;
    const paidRaw = String(adminAmountPaid).replace(',', '.').trim();
    const paid = paidRaw === '' ? 0 : Number(paidRaw);
    const paidNum = Number.isFinite(paid) ? paid : 0;
    const remaining = Math.max(0, Math.round((totalAgreed - paidNum) * 100) / 100);
    return { nights, totalAgreed, paid: paidNum, remaining };
  }, [dateRange, adminTotalRent, adminAmountPaid]);

  const resetBookingFormFields = () => {
    setAdminLabel('');
    setAdminTotalRent('');
    setAdminAmountPaid('');
    setDateRange([{ startDate: new Date(), endDate: addDays(new Date(), 7), key: 'selection' }]);
  };

  const openNewBooking = () => {
    setEditingBookingId(null);
    resetBookingFormFields();
    setShowBookingForm(true);
    setAdminError('');
  };

  const openEditBooking = (booking) => {
    setEditingBookingId(String(booking._id));
    setAdminLabel(booking.label || '');
    setAdminTotalRent(
      booking.totalRent != null && booking.totalRent !== ''
        ? String(booking.totalRent)
        : legacyWeeklyToTotal(booking)
    );
    setAdminAmountPaid(
      booking.amountPaid != null && booking.amountPaid !== '' ? String(booking.amountPaid) : ''
    );
    setDateRange([
      {
        startDate: new Date(booking.startDate),
        endDate: new Date(booking.endDate),
        key: 'selection',
      },
    ]);
    setShowBookingForm(true);
    setAdminError('');
  };

  const closeBookingForm = () => {
    setShowBookingForm(false);
    setEditingBookingId(null);
    resetBookingFormFields();
    setAdminError('');
  };

  const closeAdminModal = () => {
    setShowAdminModal(false);
    setAdminError('');
    setShowBookingForm(false);
    setEditingBookingId(null);
    resetBookingFormFields();
  };

  // Neue Buchung speichern / bestehende aktualisieren
  const handleSaveBooking = async () => {
    setAdminError('');
    setAdminLoading(true);
    const basePayload = {
      startDate: dateRange[0].startDate,
      endDate: dateRange[0].endDate,
      label: adminLabel || 'Belegt',
      totalRent: adminTotalRent.trim() === '' ? null : adminTotalRent,
      amountPaid: adminAmountPaid.trim() === '' ? null : adminAmountPaid,
    };
    try {
      const res = await fetch('/api/bookings', {
        method: editingBookingId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          editingBookingId
            ? { id: editingBookingId, password: adminPassword, ...basePayload }
            : { password: adminPassword, ...basePayload }
        ),
      });
      const data = await res.json();
      if (!res.ok) {
        setAdminError(data.error || 'Fehler beim Speichern');
      } else {
        await fetchBookings();
        closeBookingForm();
      }
    } catch (e) {
      setAdminError('Verbindungsfehler');
    } finally {
      setAdminLoading(false);
    }
  };

  // Buchung löschen
  const handleDeleteBooking = async (bookingId) => {
    try {
      const res = await fetch('/api/delete-booking', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: bookingId, password: adminPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAdminError(data.error || 'Fehler beim Löschen');
      } else {
        if (String(bookingId) === editingBookingId) {
          closeBookingForm();
        }
        await fetchBookings();
      }
    } catch (e) {
      setAdminError('Verbindungsfehler');
    }
  };

  const handleUnlock = async () => {
    setAdminError('');
    setAdminLoading(true);
    try {
      const res = await fetch('/api/verify-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        saveAdminSession(adminPassword);
        setAdminUnlocked(true);
      } else {
        setAdminError(data.error || 'Falsches Passwort');
      }
    } catch (e) {
      setAdminError('Verbindungsfehler');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleAdminLogout = () => {
    clearAdminSession();
    setAdminUnlocked(false);
    setAdminPassword('');
    setAdminError('');
    setShowBookingForm(false);
    setEditingBookingId(null);
    resetBookingFormFields();
  };

  const nextMonth = () => {
    if (currentMonth === 12) { setCurrentMonth(1); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
  };

  const prevMonth = () => {
    if (currentMonth === 1) { setCurrentMonth(12); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
  };

  const property = {
    title: "MAS CAPON",
    subtitle: "SAINT RÉMY DE PROVENCE",
    location: "Saint-Rémy-de-Provence",
    images: [
      "/images/properties/mas-capon/web_house_prime.jpeg",
      "/images/properties/mas-capon/web_pool.jpeg",
      "/images/properties/mas-capon/web_brunnen_garten.jpeg",
      "/images/properties/mas-capon/web_kitchen.jpeg",
      "/images/properties/mas-capon/web_livingroom.jpeg"
    ],
    overview: { guests: 8, bedrooms: 4, bathrooms: 3, hasPool: true },
    specs: { livingSpace: "275 m²", landArea: "2250 m²", showers: 3, toilets: 4 },
    description: "Nur wenige Kilometer vom malerischen Saint-Rémy-de-Provence entfernt liegt das Mas Capon. Das liebevoll renovierte ehemalige Bauernhaus verbindet authentischen provenzalischen Charme mit moderner Architektur und schafft so eine stimmige Mischung aus Tradition und zeitgemäßem Komfort.",
    uniqueFeatures: [
      "Nur 5 km vom Zentrum von Saint-Rémy-de-Provence entfernt",
      "Authentisches, modernisiertes Landhaus im provenzalischen Stil",
      "Weitläufige Räume und Verarbeitung mit hochwertigen Materialien"
    ],
    interior: [
      "Klimaanlage in den Schlafzimmern im ersten Stock und in der Küche/Esszimmer",
      "Fußbodenheizung und -kühlung im Erdgeschoss",
      "Voll ausgestattete Küche mit Essbereich (Tisch für 10 Personen)",
      "Holzofen",
      "Hauswirtschaftsraum mit Waschmaschine und Trockner",
      "Wohnzimmer mit Kamin und Fernseher",
      "Galerie im Wohnzimmer mit Büro, E-Piano und Drucker",
      "Glasfaser-WLAN",
      "Gäste-WC im Erdgeschoss"
    ],
    exterior: [
      "Terrasse mit Esstisch für 8 Personen",
      "Pool-Haus mit Gartenmöbeln",
      "Spüle, Kühlschrank, Weber-Gasgrill, Pizzaofen",
      "Außendusche, WC",
      "Beheizter Salzwasserpool 15m x 3m mit elektrischer Rollabdeckung",
      "Sonnenliegen",
      "Parkplatz für 3 Autos auf dem Grundstück",
      "Elektrisches Einfahrtstor"
    ],
    services: [
      "Frische Bettwäsche & Poolhandtücher im Preis enthalten",
      "Haustiere auf Anfrage",
      "Garten- und Poolpflege",
      "Wäschewechsel bei Aufenthalten ab 15 Tagen mit Zwischenreinigung",
      "Endreinigung"
    ]
  };

  return (
    <div className="min-h-screen bg-provence-limestone">
      {/* Hero Section */}
      <div className="relative h-[65vh] bg-provence-stone/10">
        <img src={property.images[0]} alt={property.title} className="w-full h-full object-cover" />
      </div>

      {/* Image Gallery Preview */}
      <div className="container mx-auto px-1 -mt-12 relative z-10 mb-10">
        <div className="flex gap-2">
          {property.images.slice(1, 5).map((image, index) => (
            <div
              key={index}
              className="relative flex-1 aspect-[4/3] cursor-pointer overflow-hidden rounded-lg shadow-[0_8px_30px_rgba(0,0,0,0.18)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.28)] transition-shadow duration-300"
            >
              <img
                src={image}
                alt={`Ansicht ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 ease-out hover:scale-105"
              />
              {index === 3 && (
                <button
                  onClick={() => navigate('/galerie')}
                  className="absolute inset-0 bg-black/40 flex items-center justify-center text-white font-sans font-medium tracking-wide text-sm hover:bg-black/55 transition-colors"
                >
                  Alle Fotos ansehen
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-serif font-bold text-provence-stone">{property.title}</h1>
          <p className="text-provence-terracotta mt-2">{property.subtitle}</p>
        </div>

        <div className="flex gap-6 mb-8">
          <div className="flex items-center gap-2"><Users className="w-5 h-5 text-provence-lavender" /><span>{property.overview.guests} Gäste</span></div>
          <div className="flex items-center gap-2"><BedDouble className="w-5 h-5 text-provence-lavender" /><span>{property.overview.bedrooms} Schlafzimmer</span></div>
          <div className="flex items-center gap-2"><Bath className="w-5 h-5 text-provence-lavender" /><span>{property.overview.bathrooms} Badezimmer</span></div>
          <div className="flex items-center gap-2"><Waves className="w-5 h-5 text-provence-lavender" /><span>Beheizter Pool</span></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="prose max-w-none">
              <p className="text-provence-stone/80 leading-relaxed">{property.description}</p>
            </div>

            <div className="bg-provence-lavender/5 p-6 rounded-lg">
              <h2 className="font-serif text-xl font-semibold text-provence-stone mb-4">Besondere Merkmale</h2>
              <ul className="space-y-2">
                {property.uniqueFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2"><span className="text-provence-lavender">•</span>{feature}</li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white rounded-xl border border-provence-lavender/20 shadow-md hover:shadow-lg transition-shadow p-6">
                <h2 className="font-serif text-xl font-semibold text-provence-stone mb-6 flex items-center gap-3">
                  <Sofa className="h-6 w-6 text-provence-lavender" />Innenbereich
                </h2>
                <ul className="space-y-3">
                  {property.interior.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2"><span className="text-provence-lavender">•</span>{feature}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-xl border border-provence-lavender/20 shadow-md hover:shadow-lg transition-shadow p-6">
                <h2 className="font-serif text-xl font-semibold text-provence-stone mb-6 flex items-center gap-3">
                  <Trees className="h-6 w-6 text-provence-lavender" />Außenbereich
                </h2>
                <ul className="space-y-3">
                  {property.exterior.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2"><span className="text-provence-lavender">•</span>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Location Section */}
            <div className="bg-white rounded-xl border border-provence-lavender/20 shadow-md p-8 space-y-8">
              <h2 className="font-serif text-2xl font-semibold text-provence-stone flex items-center gap-3">
                <MapPin className="h-7 w-7 text-provence-lavender" />Lage & Umgebung
              </h2>
              <div className="relative aspect-[21/9] rounded-xl overflow-hidden shadow-md">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2875.9866000465223!2d4.831788776191716!3d43.789344971011265!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b5e53c54bcd0dd%3A0x40819a5fd970540!2sSaint-R%C3%A9my-de-Provence%2C%20Frankreich!5e0!3m2!1sde!2sde!4v1709925131138!5m2!1sde!2sde"
                  className="absolute inset-0 w-full h-full border-0"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-provence-stone/5 rounded-xl p-6 border border-provence-lavender/10 hover:shadow-md transition-shadow">
                  <h3 className="font-serif text-lg text-provence-stone/80 mb-4">Entfernungen</h3>
                  <ul className="space-y-3">
                    {[
                      'Saint-Rémy-de-Provence: 4 km',
                      'Saint-Etienne-du-Grès: 7 km',
                      'Les Baux-de-Provence: 8 km',
                      'Maussane-les-Alpilles: 10 km',
                      'Eygalières: 12 km',
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-provence-stone/70">
                        <MapPin className="h-4 w-4 text-provence-lavender" /><span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-provence-stone/5 rounded-xl p-6 border border-provence-lavender/10 hover:shadow-md transition-shadow">
                  <h3 className="font-serif text-lg text-provence-stone/80 mb-4">Verkehrsanbindung</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2 text-provence-stone/70"><Plane className="h-4 w-4 text-provence-lavender" /><span>Flughafen Marseille: 45 Minuten</span></li>
                    <li className="flex items-center gap-2 text-provence-stone/70"><Train className="h-4 w-4 text-provence-lavender" /><span>TGV-Bahnhof Avignon: 30 Minuten</span></li>
                  </ul>
                </div>
              </div>
              <div className="bg-provence-stone/5 rounded-xl p-6 border border-provence-lavender/10 hover:shadow-md transition-shadow">
                <p className="text-provence-stone/80 leading-relaxed">
                  Die Region rund um Saint-Rémy-de-Provence wird geprägt von den Alpilles, einer eindrucksvollen Landschaft aus Hügeln, Olivenhainen und Weinbergen. In unmittelbarer Nähe laden malerische Orte wie Les Baux-de-Provence mit seiner mittelalterlichen Festung oder das charmante Eygalières mit weitem Blick ins Tal zu Erkundungen ein. Historische Sehenswürdigkeiten, unberührte Natur und die kulinarischen Genüsse der Provence machen Saint-Rémy zu einem besonderen Reiseziel.
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white rounded-lg shadow-lg p-6 border border-provence-lavender/10">
              <div className="space-y-6">
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-provence-stone mb-3">Leistungen</h2>
                  <ul className="space-y-2">
                    {property.services.map((service, index) => (
                      <li key={index} className="flex items-center gap-2 text-lg">
                        <span className="text-provence-lavender">•</span>{service}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm text-provence-stone/70">Wohnfläche</label>
                      <p className="font-semibold">{property.specs.livingSpace}</p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm text-provence-stone/70">Grundstücksfläche</label>
                      <p className="font-semibold">{property.specs.landArea}</p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/kontakt')}
                  className="w-full bg-provence-lavender text-white py-3 rounded-md hover:bg-provence-lavender/90 transition-colors"
                >
                  Kontakt aufnehmen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Section */}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-xl border border-provence-lavender/20 shadow-md p-8 space-y-8">
          <div className="flex justify-between items-center">
            <h2 className="font-serif text-2xl font-semibold text-provence-stone flex items-center gap-3">
              <Calendar className="h-7 w-7 text-provence-lavender" />
              Verfügbarkeit
            </h2>
            <button
              type="button"
              onClick={() => setShowAdminModal(true)}
              className={`p-2 rounded-full transition-colors ${
                adminUnlocked
                  ? 'text-provence-lavender bg-provence-lavender/10 hover:bg-provence-lavender/15'
                  : 'text-provence-stone/30 hover:text-provence-stone/60 hover:bg-provence-stone/5'
              }`}
              title={adminUnlocked ? 'Verwaltung (angemeldet)' : 'Verwaltung'}
            >
              {adminUnlocked ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            </button>
          </div>

          {/* Calendar Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-2 hover:bg-provence-stone/5 rounded-full transition-colors">
              <ChevronLeft className="h-6 w-6 text-provence-stone/70" />
            </button>
            <button onClick={nextMonth} className="p-2 hover:bg-provence-stone/5 rounded-full transition-colors">
              <ChevronRight className="h-6 w-6 text-provence-stone/70" />
            </button>
          </div>

          {/* Three Months Calendar Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[-1, 0, 1].map((offset) => {
              const baseIndex = currentMonth - 1;
              const rawIndex = baseIndex + offset;
              const monthIndex = (rawIndex + 12) % 12;
              const yearOffset = rawIndex >= 12 ? 1 : rawIndex < 0 ? -1 : 0;
              const displayYear = currentYear + yearOffset;

              return (
                <div key={offset} className="space-y-2">
                  <h4 className="font-serif text-lg text-provence-stone text-center mb-2">
                    {months[monthIndex]} {displayYear}
                  </h4>
                  <div className="grid grid-cols-7 gap-1 text-sm">
                    {weekDays.map((day, index) => (
                      <div key={index} className="text-center font-medium text-provence-stone/70 py-1">{day}</div>
                    ))}
                    {Array.from({ length: getStartDayOfMonth(displayYear, monthIndex) }).map((_, i) => (
                      <div key={`empty-${i}`} />
                    ))}
                    {Array.from({ length: getDaysInMonth(displayYear, monthIndex) }, (_, i) => i + 1).map((day) => {
                      const dateStr = `${displayYear}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const unavailable = unavailableDates.has(dateStr);
                      const bookingForDay = adminUnlocked && unavailable ? dateToBooking.get(dateStr) : null;
                      return (
                        <div
                          key={day}
                          role={bookingForDay ? 'button' : undefined}
                          tabIndex={bookingForDay ? 0 : undefined}
                          onMouseEnter={(e) => {
                            if (!bookingForDay) return;
                            setCalendarHover({
                              booking: bookingForDay,
                              x: e.clientX,
                              y: e.clientY,
                            });
                          }}
                          onMouseMove={(e) => {
                            if (!bookingForDay) return;
                            setCalendarHover((h) =>
                              h ? { booking: h.booking, x: e.clientX, y: e.clientY } : null
                            );
                          }}
                          onMouseLeave={() => setCalendarHover(null)}
                          onClick={() => {
                            if (!adminUnlocked || !bookingForDay) return;
                            setShowAdminModal(true);
                            openEditBooking(bookingForDay);
                          }}
                          className={`aspect-square rounded-sm flex items-center justify-center text-xs transition-shadow
                            ${unavailable ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}
                            ${
                              bookingForDay && adminUnlocked
                                ? 'cursor-pointer ring-1 ring-red-200/60 hover:ring-provence-lavender/40 hover:shadow-sm'
                                : bookingForDay
                                  ? 'cursor-help ring-1 ring-red-200/60 hover:ring-provence-lavender/40 hover:shadow-sm'
                                  : ''
                            }`}
                        >
                          {day}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-col items-center gap-3 mt-4">
            <div className="flex flex-wrap gap-6 text-sm justify-center">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-100" />
                <span className="text-provence-stone/70">Verfügbar</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-100" />
                <span className="text-provence-stone/70">Belegt</span>
              </div>
            </div>
            {adminUnlocked && (
              <p className="text-xs text-provence-stone/50 text-center max-w-md">
                Belegte Tage antippen zum Bearbeiten. Mit der Maus darüberfahren zeigt Gast und Finanzen.
              </p>
            )}
          </div>
        </div>
      </div>

      {calendarHover && adminUnlocked && (
        <BookingHoverCard calendarHover={calendarHover} />
      )}

      {/* Admin Modal */}
      {showAdminModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center gap-3 p-6 border-b border-provence-lavender/20">
              <h3 className="font-serif text-xl font-semibold text-provence-stone">
                {adminUnlocked ? 'Belegung verwalten' : 'Geschützter Bereich'}
              </h3>
              <div className="flex items-center gap-2">
                {adminUnlocked && (
                  <button
                    type="button"
                    onClick={handleAdminLogout}
                    className="p-2 rounded-full text-provence-stone/70 hover:bg-provence-stone/5 transition-colors"
                    title="Abmelden"
                    aria-label="Abmelden"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeAdminModal}
                  className="p-2 hover:bg-provence-stone/5 rounded-full transition-colors"
                  aria-label="Schließen"
                >
                  <X className="h-5 w-5 text-provence-stone/70" />
                </button>
              </div>
            </div>

            {/* Passwort-Screen */}
            {!adminUnlocked ? (
              <div className="p-8 space-y-5">
                <p className="text-sm text-provence-stone/60 text-center">
                  Bitte Passwort eingeben um fortzufahren.
                </p>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => { setAdminPassword(e.target.value); setAdminError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && adminPassword) handleUnlock(); }}
                  placeholder="••••••••"
                  autoFocus
                  className="w-full border border-provence-lavender/30 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-provence-lavender/40"
                />
                {adminError && <p className="text-red-600 text-sm">{adminError}</p>}
                <button
                  onClick={handleUnlock}
                  disabled={adminLoading || !adminPassword}
                  className="w-full bg-provence-lavender text-white py-2.5 rounded-md hover:bg-provence-lavender/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {adminLoading ? 'Prüfe…' : 'Entsperren'}
                </button>
              </div>
            ) : (

            /* Verwaltung (angemeldet) */
            <div className="p-6 space-y-6">
              {showBookingForm && (
                <div className="space-y-6">
                  <button
                    type="button"
                    onClick={closeBookingForm}
                    className="text-sm text-provence-stone/70 hover:text-provence-stone transition-colors"
                  >
                    ← Zurück zur Übersicht
                  </button>

                  <div>
                    <p className="text-sm font-medium text-provence-stone/70 mb-3">Zeitraum</p>
                    <div className="flex justify-center overflow-x-auto rounded-lg border border-provence-lavender/15 bg-provence-stone/[0.02] py-2">
                      <DateRange
                        editableDateInputs={true}
                        onChange={(item) => setDateRange([item.selection])}
                        moveRangeOnFirstSelection={false}
                        ranges={dateRange}
                        rangeColors={['#7B6FAD']}
                        locale={de}
                      />
                    </div>
                    <p className="text-xs text-provence-stone/45 mt-2 text-center">
                      Vergangene Zeiträume können Sie für Nachbuchungen erfassen.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-provence-stone/80">
                      Gast / Bezeichnung
                    </label>
                    <input
                      type="text"
                      value={adminLabel}
                      onChange={(e) => setAdminLabel(e.target.value)}
                      placeholder="z. B. Familie Müller"
                      className="w-full border border-provence-lavender/30 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-provence-lavender/40"
                    />
                  </div>

                  <div className="rounded-xl border border-provence-lavender/20 bg-provence-lavender/[0.04] p-5 space-y-4">
                    <div className="flex items-center gap-2 text-provence-stone">
                      <Wallet className="h-4 w-4 text-provence-lavender shrink-0" />
                      <span className="text-sm font-semibold">Finanzen</span>
                      <span className="text-xs text-provence-stone/50 font-normal">(optional)</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:items-end">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-provence-stone/70 min-h-[2.5rem] sm:min-h-0 flex items-end pb-0">
                          Vereinbarter Gesamtpreis der Vermietung
                        </label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-provence-stone/40 text-sm">
                            €
                          </span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={adminTotalRent}
                            onChange={(e) => setAdminTotalRent(e.target.value)}
                            placeholder="z. B. 8400"
                            className="w-full h-11 border border-provence-lavender/30 rounded-md pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-provence-lavender/40"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-provence-stone/70 min-h-[2.5rem] sm:min-h-0 flex items-end pb-0">
                          Bereits angezahlt
                        </label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-provence-stone/40 text-sm">
                            €
                          </span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={adminAmountPaid}
                            onChange={(e) => setAdminAmountPaid(e.target.value)}
                            placeholder="z. B. 1000"
                            className="w-full h-11 border border-provence-lavender/30 rounded-md pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-provence-lavender/40"
                          />
                        </div>
                      </div>
                    </div>
                    {previewFinancials?.totalAgreed != null && (
                      <div className="rounded-lg bg-white/80 border border-provence-lavender/15 px-4 py-3 text-sm space-y-2">
                        <div className="flex justify-between text-provence-stone/80">
                          <span>Aufenthalt</span>
                          <span className="tabular-nums">{previewFinancials.nights} Nächte</span>
                        </div>
                        <div className="flex justify-between font-medium text-provence-stone">
                          <span>Gesamt</span>
                          <span className="tabular-nums">{formatEur(previewFinancials.totalAgreed)}</span>
                        </div>
                        <div className="flex justify-between text-provence-stone/80">
                          <span>Bereits angezahlt</span>
                          <span className="tabular-nums">{formatEur(previewFinancials.paid)}</span>
                        </div>
                        <div className="flex justify-between pt-1 border-t border-provence-lavender/15 font-semibold text-provence-lavender">
                          <span>Noch offen</span>
                          <span className="tabular-nums">{formatEur(previewFinancials.remaining)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {adminError && <p className="text-red-600 text-sm">{adminError}</p>}

                  <button
                    type="button"
                    onClick={handleSaveBooking}
                    disabled={adminLoading}
                    className="w-full bg-provence-lavender text-white py-3 rounded-md text-sm font-medium hover:bg-provence-lavender/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    {adminLoading
                      ? 'Speichern…'
                      : editingBookingId
                        ? 'Änderungen speichern'
                        : 'Belegung speichern'}
                  </button>
                </div>
              )}

              {!showBookingForm && (
                <button
                  type="button"
                  onClick={openNewBooking}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-provence-lavender/35 bg-provence-lavender/[0.06] text-provence-stone py-3.5 px-4 text-sm font-medium hover:bg-provence-lavender/12 hover:border-provence-lavender/50 transition-colors"
                >
                  <Plus className="h-4 w-4 text-provence-lavender shrink-0" />
                  Neue Belegung hinzufügen
                </button>
              )}

              {bookings.length > 0 && (
                <div
                  className={`space-y-3 ${showBookingForm ? 'border-t border-provence-lavender/20 pt-6' : ''}`}
                >
                  <p className="text-sm font-semibold text-provence-stone">Bestehende Buchungen</p>
                  <ul className="space-y-3">
                    {bookings.map((booking) => {
                      const fin = getBookingFinancials(booking);
                      return (
                        <li
                          key={booking._id}
                          className="rounded-lg border border-provence-stone/10 bg-provence-stone/[0.03] px-4 py-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <button
                              type="button"
                              onClick={() => {
                                setShowAdminModal(true);
                                openEditBooking(booking);
                              }}
                              className="min-w-0 flex-1 text-left rounded-md -m-1 p-1 hover:bg-provence-stone/5 transition-colors"
                            >
                              <p className="font-medium text-provence-stone text-sm truncate">
                                {booking.label || 'Belegt'}
                              </p>
                              <p className="text-provence-stone/55 text-xs mt-0.5">
                                {format(new Date(booking.startDate), 'dd.MM.yyyy')} –{' '}
                                {format(new Date(booking.endDate), 'dd.MM.yyyy')}
                              </p>
                              {fin.hasFinance && (
                                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-provence-stone/70">
                                  <span>Gesamt</span>
                                  <span className="text-right tabular-nums">{formatEur(fin.totalAgreed)}</span>
                                  <span>Angezahlt</span>
                                  <span className="text-right tabular-nums">{formatEur(fin.paid)}</span>
                                  <span className="font-medium text-provence-stone">Offen</span>
                                  <span className="text-right tabular-nums font-medium text-provence-lavender">
                                    {formatEur(fin.remaining)}
                                  </span>
                                </div>
                              )}
                            </button>
                            <div className="flex shrink-0 items-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowAdminModal(true);
                                  openEditBooking(booking);
                                }}
                                className="p-1.5 text-provence-lavender/80 hover:text-provence-lavender hover:bg-provence-lavender/10 rounded-md transition-colors"
                                title="Bearbeiten"
                              >
                                <PenLine className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteBooking(booking._id)}
                                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                title="Buchung löschen"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
